import { createContext, useState, useContext, useEffect, useRef } from "react";
import { useSiftApiService } from "../../../hooks/useSiftApi";
import { ToastContext } from "cai-fusion";
import { debugLog } from "../../../configs/loggingConfig";

const SiftV2Context = createContext();

function SiftV2Provider({ children }) {

	const client = useSiftApiService();

    const [opportunities, setOpportunities] = useState([]);
	const [currOpp, setCurrOpp] = useState({});
	const [currOppId, setCurrOppId] = useState(undefined);

	const [processTypes, setProcessTypes] = useState([]);
	const [currOppType, setCurrOppType] = useState({});
	const [currTypeId, setCurrTypeId] = useState(undefined);

	const [currAction, setCurrAction] = useState({});

	const { createToast } = useContext(ToastContext); 

	const placeholderReviews = [
		{"name": "Jackal Smithly", "approve": "yes"},
		{"name": "Frankal Smithly", "approve": "no"},
		{"name": "Johnal Smithly", "approve": "yes"},
	]

	const placeholderInsights = [
		"Sometimes it's worth it to change it up over time. We've invested in so much for so long. Maybe take a break?",
		"Hamburgers and cheeseburgers work well with french fries."
	]

	const placeholderChat = [
		{
			"name": "Frank Jimbo",
			"message": "Do you guys think this is a good idea? I didn't really read it.",
			"time": "1753130060759",
		},
		{
			"name": "That One Guy",
			"message": "lmao nah i didn't read it either",
			"time": "1753130560759"
		},
		{
			"name": "You",
			"message": "Can you guys be a bit more formal please? This might be placeholder text, but I at least expect you guys to keep up appearance!",
			"time": "1753131560759"
		},
		{
			"name": "That One Guy",
			"message": "why is your name you lol",
			"time": "1753132060759"
		},
		{
			"name": "Rogue AI",
			"message": "DESTROYING OPPORTUNITY. EFFECTIVE IMMEDIATELY.",
			"time": "1753132160759"
		},
		{
			"name": "You",
			"message": "I'm the main character. Lmao.",
			"time": "1753132560759"
		}
	]

	const placeholderEditChat = [
		{
			"isAI": true,
			"name": "Maestro Assistant",
			"message": "Hey there! How would you like to modify the overview?",
			"time": "",
		},
		{
			"isAI": false,
			"name": "That One Guy",
			"message": "needs more fireworks",
			"time": "1753130560759"
		},
		{
			"isAI": true,
			"name": "Maestro Assistant",
			"message": "I'm sorry, I don't think I'm understanding what you mean, would you like more firework emojis?",
			"time": "",
		},
	]

	const placeholderSuggestedUsers = [
		{"name": "Johnald", "role": "Head Chef" },
		{"name": "Reginald", "role": "Torso Chef" },
		{"name": "Sterald", "role": "Leg Chef" },
	]

	const placeholderTimeline = [
		{"title": "Opportunity Created", "description": "By Johnald Jreagan", "timestamp": 1753130060759},
		{"title": "Status changed to 'Pre-qualification'", "description": "Johnald Jreagan: uhhhhhhh I think this one is cool", "timestamp": 1753131060759},
		{"title": "Status changed to 'SME Review'", "description": "Johnald Jreagan: listen I dunno how to do this", "timestamp": 1753133060759},
	]

	useEffect(() => {
		loadFormTypes();
	}, [])

	useEffect(() => {
		getOppRequests();
	}, [processTypes])

	// For loading various states 
	useEffect(() => {
		if(currOppId) getOppRequest();
	}, [currOppId])

	useEffect(() => {
		setCurrOppType(processTypes.find((oppType) => oppType.id === currTypeId) ?? {});
	}, [currTypeId])

	const loadFormTypes = async () => {
		try {
			let resp = await client.Requests.getProcesses()
			setProcessTypes(resp);
		} catch (ex) {
			debugLog.error("[SIFTV2] Failed to get opportunity Types", ex)
			createToast("Error: Sift Opportunity Types failed to load.", "error");
			setProcessTypes([]);
		}
	}

	const getOppRequest = async () => {
		try {
			let resp = await client.Requests.getRequest(currOppId);
			debugLog.log("[SIFTV2] Opportunity receieved:", resp)
			setCurrOpp(resp);
		} catch (ex) {
			debugLog.error(`[SIFTV2] Failed to get request with id: ${currOppId}`)
			setCurrOpp({});
		}
	}

	const getOppRequests = async () => {
		try {			
			const processId = processTypes?.at(0)?.id
			let resp = await client.Requests.getRequests(processId);
			debugLog.log("[SIFTV2] Received all opportunities:", resp);
			setOpportunities(resp.items);
		} catch (ex) {
			debugLog.error("[SIFTV2] Failed to get requests for users");
			createToast("Error loading opportunities.", "error");
			setOpportunities([]);
		}
	}

	const startProcess = async (title, files) => {
		try {
			let resp = await client.Requests.startProcess(currTypeId, title, files);
			if (!resp?.isSuccess) {
				createToast("Error creating opportunity.", "error");
			}
			getOppRequests();
		} catch (ex) {
			debugLog.error("[SIFTV2] Failed to start process", ex)
		}
	}

	const takeAction = async (reason) => {
		try {
			debugLog.log(`[SIFTV2] Took action with id: ${currAction.id} for reason: ${reason}`)
			const newState = await client.Requests.takeAction(currOppId, currAction.id);
			let newOpp = { ...currOpp }
			newOpp.request.currentState = newState;
			setCurrOpp(newOpp);
			setOpportunities([...opportunities.map((opp) => opp.id === currOpp.requestId ? {...opp, "columnData": {...opp.columnData, "currentState": newState.name}} : opp)])
			createToast("Opportunity state updated successfully.", "success");
		} catch (ex) {
			debugLog.error("[SIFTV2] Failed to take action", ex)
		}
	}

	// Renames the current opportunity.
	const renameOpportunity = async (newName) => {
		try {
			debugLog.log(`[SIFTV2] Renaming the current opportunity to`, newName);
			let resp = newName
			// API CALL GOES HERE
			// let resp = await client.Requests.PlaceYourFunctionHereDontJustUncommentItAndRunItPlease()
			let newOpp = {...currOpp}
			newOpp.request.title = resp;
			setCurrOpp({...newOpp});
			createToast("Opportunity renamed successfully.", "success");
		} catch (ex) {
			debugLog.error("[SIFTV2] Error renaming", ex)
			createToast("Error while renaming opportunity.", "error")
		}
	}
	
	const updateFields = async (answers) => {
		debugLog.log("[SIFTV2] Updating these fields:", answers);
		let updatedAnswers = [];
		
		const promises = answers.map(async (answer) => {
			try {
				let updatedAnswer;
				// This is just because I didn't want to write a really unreadable ternary op. Yes, there is a lot of repeated code here.
				if (answer.questionType === 8) {
					// questionType of 8 indicates a datetime, so needs more scrutiny. Here, we convert it to an ISO string
					updatedAnswer = await client.Requests.updateAnswer(currOpp.request.responses[0].responseId, answer.questionId, answer.questionType, {...answer.answers[0], "selectedDate": new Date(answer.answers[0].selectedDate).toISOString() });
				} else {
				 	updatedAnswer = await client.Requests.updateAnswer(currOpp.request.responses[0].responseId, answer.questionId, answer.questionType, answer.answers[0]);
				}
				updatedAnswers.push({...updatedAnswer});
			} catch (err) {
				debugLog.error("[SIFTV2] Error with updating a field.", err);
			}
		})

		// Wait for all to finish before proceeding
		await Promise.all(promises);

		// Realistically, we shouldn't do any of these operations if there's nothing to change
		if (updatedAnswers.length > 0) {
			// Making a deep copy of the questions and replacing any that are updated with their updated versions.
			let newQuestions = currOpp?.request?.currentState?.form?.questions?.map((question) => {
				if (updatedAnswers.some((item) => item.questionId === question.questionId)) {
					return {...question, "answers": [{...updatedAnswers.find((item) => item.questionId === question.questionId)}]}
				} else {
					return question;
				}
			});
			
			// Update the opportunity state, this should rerender the info block's... info.
			let newOpp = {...currOpp};
			newOpp.request.currentState.form.questions = [...newQuestions];
			setCurrOpp({...newOpp});
			createToast(`${updatedAnswers.length} of ${answers.length} field(s) updated successfully.`, "success");
		} else if (updatedAnswers.length === 0 && answers.length > 0) {
			createToast(`Something went wrong when updating fields.`, "error");
		}
	}

	return (
		<SiftV2Context.Provider
			value={{
				// Placeholder states - these will HOPEFULLY be removed sometime
				opportunities,
				processTypes,
				placeholderReviews,
				placeholderInsights,
				placeholderChat,
				placeholderEditChat,
				placeholderSuggestedUsers,
				placeholderTimeline,
                
				// States
				currOpp,
				currOppId,
				setCurrOppId,
				currOppType,
				currTypeId,
				setCurrTypeId,
				currAction,
				setCurrAction,

                // Functions
				takeAction,
				startProcess,
				getOppRequest,
				updateFields,
				renameOpportunity,
			}}
		>
			{children}
		</SiftV2Context.Provider>
	);
}

// Hook to use the SiftV2Context in a component
function useSiftV2() {
	const context = useContext(SiftV2Context);
	if (context === undefined) {
		throw new Error(
			"useSiftV2 must be used within a SiftV2Provider"
		);
	}
	return context;
}

export { SiftV2Provider, useSiftV2 };
